import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useConfig } from '@/contexts/ConfigContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useUI } from '@/contexts/UIContext';
import ModuleContainer from './ModuleContainer';
import { RADIO_COMMANDS } from '@/constants/radioCommands';
import { Restore, Save } from '@mui/icons-material';
import KeySelector from '@/components/CustomInput/KeySelector';
import { getItemIcon } from '@/constants/itemIcons';
import Image from 'next/image';
import PublicIcon from '@mui/icons-material/Public';
import GroupIcon from '@mui/icons-material/Group';
import RadioIcon from '@mui/icons-material/Radio';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import ForumIcon from '@mui/icons-material/Forum';
import CloseIcon from '@mui/icons-material/Close';



const TopSection = styled.div`
    display: flex;
    gap: 40px;
    flex: 1;
    min-height: 500px;
`;

const BindsSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
    margin-bottom: 16px;
`;

const VisualBarContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;

    &::before {
        content: '${({ $label }) => $label || ''}';
        font-size: 0.7rem;
        font-weight: 800;
        color: ${({ $color }) => $color || '#6366f1'};
        opacity: 0.9;
        letter-spacing: 2px;
        padding-left: 2px;
        text-transform: uppercase;
    }
`;

const VisualBar = styled.div`
    display: flex;
    background: ${({ $bg }) => $bg || 'rgba(0, 0, 0, 0.2)'};
    border: 1px solid ${({ $color }) => $color ? `${$color}44` : 'rgba(255, 255, 255, 0.05)'};
    border-radius: 12px;
    overflow: hidden;
    padding: 24px 0;
    transition: all 0.3s ease;
    flex-wrap: wrap;
    align-items: flex-end;
    width: 100%;
    
    &:hover {
        border-color: ${({ $color }) => $color ? `${$color}88` : 'rgba(255, 255, 255, 0.1)'};
        background: ${({ $bg, $color }) => $bg ? `${$bg.replace('0.2', '0.25')}` : ($color ? `${$color}11` : 'rgba(255, 255, 255, 0.03)')};
    }
`;

const BarSlot = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    border-right: 1px solid rgba(255, 255, 255, 0.05);
    transition: all 0.2s;
    padding: 0px 10px;
    min-width: 100px;

    &:last-child {
        border-right: none;
    }
`;

const WeaponIconWrapper = styled.div`
    position: relative;
    width: 100%;
    max-width: 50px;
    height: 25px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 2px;
    
    img {
        object-fit: contain;
        filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5));
        opacity: 0.9;
    }

    svg {
        font-size: 20px;
        color: rgba(255, 255, 255, 0.9);
        filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5));
    }
`;

const SlotLabelLocal = styled.span`
    font-size: 0.65rem;
    color: rgba(255, 255, 255, 0.75);
    font-weight: 700;
    text-transform: uppercase;
    text-align: center;
`;

const CommandLabel = styled.span`
    font-size: 0.5rem;
    color: rgba(255, 255, 255, 0.4);
    margin-top: -6px;
    margin-bottom: 4px;
    line-height: 1.2;
    text-transform: uppercase;
    max-width: 90%;
    text-align: center;
`;


const TabContainer = styled.div`
    display: flex;
    gap: 10px;
    background: rgba(0, 0, 0, 0.2);
    padding: 5px;
    border-radius: 8px;
    width: fit-content;
`;

const TabButton = styled.button`
    padding: 8px 16px;
    background: ${props => props.$active ? 'rgba(99, 102, 241, 0.2)' : 'transparent'};
    color: ${props => props.$active ? '#fff' : 'rgba(255, 255, 255, 0.6)'};
    border: 1px solid ${props => props.$active ? '#6366f1' : 'transparent'};
    border-radius: 6px;
    cursor: pointer;
    font-family: 'Chakra Petch', sans-serif;
    font-weight: 600;
    transition: all 0.2s;

    &:hover {
        background: rgba(99, 102, 241, 0.1);
        color: #fff;
    }
`;

const SlotNumber = styled.span`
    font-size: 10px;
    color: rgba(255, 255, 255, 0.4);
    position: absolute;
    top: 4px;
    right: 6px;
`;

const SlotLabel = styled.span`
    font-size: 10px;
    font-weight: 600;
    color: #fff;
    line-height: 1.2;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
`;

const ContentArea = styled.div`
    display: flex;
    gap: 40px;
    flex: 1;
    padding-left: 20px;
`;

const WheelSection = styled.div`
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    background: transparent;
    border-radius: 20px;
`;

const WheelContainer = styled.div`
    position: relative;
    width: 420px;
    height: 420px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.1);
`;

const CenterHub = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 80px;
    height: 80px;
    //background: #1e1b2e;
    border-radius: 50%;
    //border: 2px solid #6366f1;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;
    box-shadow: 0 0 20px rgba(241, 149, 99, 0.1);
`;

const ClearButton = styled.div`
    position: absolute;
    top: -6px;
    right: -6px;
    width: 24px;
    height: 24px;
    background: #ef4444;
    border-radius: 50%;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 2px 5px rgba(0,0,0,0.3);
    border: 2px solid #1e1b2e;
    opacity: 0;
    transform: scale(0.8);
    transition: all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    z-index: 50;

    &:hover {
        background: #dc2626;
        transform: scale(1.1);
    }
`;

const WheelSlot = styled.div`
    position: absolute;
    width: 100px;
    height: 100px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s;
    background: ${props => props.$hasValue ? 'rgba(249, 115, 22, 0.1)' : (props.$active ? 'rgba(99, 102, 241, 0.1)' : 'rgba(30, 27, 46, 0.5)')};
    border: 1px solid ${props => props.$active ? '#ffffff34' : (props.$hasValue ? 'rgba(249, 115, 22, 0.1)' : 'rgba(255, 255, 255, 0)')};
    border-radius: 12px;
    padding: 8px;
    box-shadow: ${props => props.$active ? 'inset 0 0 30px rgba(255, 255, 255, 0.2)' : 'inset 0 0 30px rgba(19, 20, 60, 0.6)'};
    z-index: 5;



    /* Positioning logic based on index 1-8 (clock starting at 12) */
    ${props => {
        const radius = 150;
        const index = props.$index - 1; // 0-7
        // 1 is Top (12:00) -> -90 deg
        const angle = -90 + (index * 45);
        const rad = angle * (Math.PI / 180);
        const x = Math.cos(rad) * radius;
        const y = Math.sin(rad) * radius;

        return `
            top: calc(50% + ${y}px - 50px);
            left: calc(50% + ${x}px - 50px);
        `;
    }}

    &:hover {
        border-color: ${props => props.$hasValue ? '#f97316' : '#6366f1'};
        transform: scale(1.05);
        background: ${props => props.$hasValue ? 'rgba(249, 115, 22, 0.25)' : 'rgba(99, 102, 241, 0.15)'};
        z-index: 20;
    }

    &:hover ${ClearButton} {
        opacity: 1;
        transform: scale(1);
    }
`;

const CommandList = styled.div`
    width: 350px;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    height: 100%;
    max-height: 404px;
`;

const CommandListHeader = styled.div`
    padding: 16px;
    background: rgba(255, 255, 255, 0.05);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    font-weight: 700;
    color: #a78bfa;
    text-transform: uppercase;
    font-size: 12px;
    letter-spacing: 1px;
    width: 100%;
`;

const CommandCategories = styled.div`
    overflow-y: auto;
    flex: 1;
    padding: 10px;
    flex-direction: column;
    width: 100%;
    
    &::-webkit-scrollbar {
        width: 6px;
    }
    &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.1);
        border-radius: 3px;
    }
`;

const CategoryBlock = styled.div`
    margin-bottom: 16px;
    flex-direction: column;
    width: 100%;
`;

const CategoryTitle = styled.div`
    font-size: 11px;
    color: rgba(255, 255, 255, 0.4);
    padding: 4px 8px;
    text-transform: uppercase;
    margin-bottom: 4px;
    font-weight: 700;
    width: 100%;
    background: rgba(147, 131, 255, 0.2);
`;

const CommandItem = styled.div`
    width: 100%;
    padding: 8px 12px;
    margin-bottom: 2px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.8);
    transition: all 0.15s;
    background: ${props => props.$selected ? 'rgba(241, 139, 99, 0.2)' : 'transparent'};
    border: 1px solid ${props => props.$selected ? 'rgba(241, 135, 99, 0.4)' : 'transparent'};

    &:hover {
        background: rgba(255, 175, 144, 0.1);
        padding-left: 16px;
    }
`;

const RadioModule = ({ id, isDemoBlocked }) => {
    const { t } = useLanguage();
    const { updateConfig, configData, configIsLocked } = useConfig();
    const [activeTab, setActiveTab] = useState(0); // 0, 1, 2 for tabs
    const [selectedSlot, setSelectedSlot] = useState(null);
    const commandRefs = useRef({});

    useEffect(() => {
        if (selectedSlot !== null) {
            const cmdId = getSlotCommandId(selectedSlot);
            if (cmdId && commandRefs.current[cmdId]) {
                commandRefs.current[cmdId].scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }
        }
    }, [selectedSlot, configData, activeTab]);

    const binds = configData.binds || {};

    const handleKeyChange = (key, value) => {
        updateConfig('binds', key, value);
    };

    // Helper to get current config value for a slot
    const getSlotCommandId = (slotIdx) => {
        // key format: cl_radial_radio_tab_0_text_1
        const key = `cl_radial_radio_tab_${activeTab}_text_${slotIdx}`;
        // Parameters are usually stored in 'settings' object within configData
        const val = configData.settings?.[key];
        // Value formats: "#Chatwheel_requestdrop"
        if (!val) return null;
        return val.replace(/^"|"$/g, ''); // Remove quotes if present
    };

    const getCommandLabel = (cmdId) => {
        if (!cmdId) return t('UNBOUND');
        for (const cat of RADIO_COMMANDS) {
            const found = cat.commands.find(c => c.id === cmdId);
            if (found) return t(found.label);
        }
        return cmdId; // Fallback to ID if not found
    };

    const handleCommandSelect = (cmdId) => {
        if (selectedSlot === null) return;

        const key = `cl_radial_radio_tab_${activeTab}_text_${selectedSlot}`;
        // Update config in 'settings' section
        if (cmdId === "") {
            updateConfig('settings', key, "");
        } else {
            updateConfig('settings', key, `"${cmdId}"`);
        }
    };

    const handleSlotClick = (idx) => {
        setSelectedSlot(idx);
    };

    return (
        <ModuleContainer
            id={id}
            moduleKey="radio"
            title={t('RADIO_COMMUNICATION_TITLE')}
            icon={RadioIcon}
            isDemoBlocked={isDemoBlocked}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>

                <BindsSection>
                    <VisualBarContainer $label={t('RADIO_CHAT_MENUS')} $color="#10b981">
                        <VisualBar $color="#10b981" $bg="rgba(16, 185, 129, 0.05)">
                            <BarSlot>
                                <WeaponIconWrapper>
                                    <RadioIcon />
                                </WeaponIconWrapper>
                                <SlotLabelLocal>{t('RADIO_MENU_1')}</SlotLabelLocal>
                                <CommandLabel>{t('RADIO_DESC_1')}</CommandLabel>
                                <KeySelector value={binds.key_radio1} onChange={(v) => handleKeyChange('key_radio1', v)} fieldPath="binds.key_radio1" disabled={configIsLocked} />
                            </BarSlot>
                            <BarSlot>
                                <WeaponIconWrapper>
                                    <RadioIcon />
                                </WeaponIconWrapper>
                                <SlotLabelLocal>{t('RADIO_MENU_2')}</SlotLabelLocal>
                                <CommandLabel>{t('RADIO_DESC_2')}</CommandLabel>
                                <KeySelector value={binds.key_radio2} onChange={(v) => handleKeyChange('key_radio2', v)} fieldPath="binds.key_radio2" disabled={configIsLocked} />
                            </BarSlot>
                            <BarSlot>
                                <WeaponIconWrapper>
                                    <RadioIcon />
                                </WeaponIconWrapper>
                                <SlotLabelLocal>{t('RADIO_MENU_3')}</SlotLabelLocal>
                                <CommandLabel>{t('RADIO_DESC_3')}</CommandLabel>
                                <KeySelector value={binds.key_radio3} onChange={(v) => handleKeyChange('key_radio3', v)} fieldPath="binds.key_radio3" disabled={configIsLocked} />
                            </BarSlot>
                            <BarSlot>
                                <WeaponIconWrapper>
                                    <DonutLargeIcon />
                                </WeaponIconWrapper>
                                <SlotLabelLocal>{t('KEY_RADIAL_RADIO')}</SlotLabelLocal>
                                <CommandLabel>{t('WHEEL_MENU')}</CommandLabel>
                                <KeySelector value={binds.key_radial_radio} onChange={(v) => handleKeyChange('key_radial_radio', v)} fieldPath="binds.key_radial_radio" disabled={configIsLocked} />
                            </BarSlot>
                            <BarSlot>
                                <WeaponIconWrapper>
                                    <DonutLargeIcon />
                                </WeaponIconWrapper>
                                <SlotLabelLocal>{t('KEY_RADIAL_RADIO2')}</SlotLabelLocal>
                                <CommandLabel>{t('WHEEL_MENU')}</CommandLabel>
                                <KeySelector value={binds.key_radial_radio2} onChange={(v) => handleKeyChange('key_radial_radio2', v)} fieldPath="binds.key_radial_radio2" disabled={configIsLocked} />
                            </BarSlot>
                            <BarSlot>
                                <WeaponIconWrapper>
                                    <DonutLargeIcon />
                                </WeaponIconWrapper>
                                <SlotLabelLocal>{t('KEY_RADIAL_RADIO3')}</SlotLabelLocal>
                                <CommandLabel>{t('WHEEL_MENU')}</CommandLabel>
                                <KeySelector value={binds.key_radial_radio3} onChange={(v) => handleKeyChange('key_radial_radio3', v)} fieldPath="binds.key_radial_radio3" disabled={configIsLocked} />
                            </BarSlot>
                        </VisualBar>
                    </VisualBarContainer>
                </BindsSection>

                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <TabContainer>
                        <TabButton $active={activeTab === 0} onClick={() => { setActiveTab(0); setSelectedSlot(null); }}>
                            {t('KEY_RADIAL_RADIO')}
                        </TabButton>
                        <TabButton $active={activeTab === 1} onClick={() => { setActiveTab(1); setSelectedSlot(null); }}>
                            {t('KEY_RADIAL_RADIO2')}
                        </TabButton>
                        <TabButton $active={activeTab === 2} onClick={() => { setActiveTab(2); setSelectedSlot(null); }}>
                            {t('KEY_RADIAL_RADIO3')}
                        </TabButton>
                    </TabContainer>
                </div>

                <ContentArea>
                    <WheelSection>
                        <WheelContainer>
                            <CenterHub>
                            </CenterHub>

                            {[1, 2, 3, 4, 5, 6, 7, 8].map((slotIdx) => {
                                const cmdId = getSlotCommandId(slotIdx);
                                const label = getCommandLabel(cmdId);
                                const hasValue = !!cmdId;

                                return (
                                    <WheelSlot
                                        key={slotIdx}
                                        $index={slotIdx}
                                        $active={selectedSlot === slotIdx}
                                        $hasValue={hasValue}
                                        onClick={() => handleSlotClick(slotIdx)}
                                    >
                                        {hasValue && (
                                            <ClearButton
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const key = `cl_radial_radio_tab_${activeTab}_text_${slotIdx}`;
                                                    updateConfig('settings', key, "");
                                                }}
                                                title={t('CLEAR_SLOT') || 'Clear Slot'}
                                            >
                                                <CloseIcon style={{ fontSize: 16 }} />
                                            </ClearButton>
                                        )}
                                        <SlotNumber>{slotIdx}</SlotNumber>
                                        <SlotLabel>{label}</SlotLabel>
                                    </WheelSlot>
                                );
                            })}
                        </WheelContainer>
                    </WheelSection>

                    <CommandList>
                        <CommandListHeader>
                            {selectedSlot ? `${t('SELECT_SLOT_COMMAND')} ${selectedSlot}` : t('SELECT_A_SLOT')}
                        </CommandListHeader>
                        <CommandCategories>

                            {RADIO_COMMANDS.map((cat, idx) => (
                                <CategoryBlock key={idx}>
                                    <CategoryTitle>{t(cat.category)}</CategoryTitle>
                                    {cat.commands.map(cmd => (
                                        <CommandItem
                                            ref={el => commandRefs.current[cmd.id] = el}
                                            key={cmd.id}
                                            onClick={() => handleCommandSelect(cmd.id)}
                                            $selected={selectedSlot !== null && getSlotCommandId(selectedSlot) === cmd.id}
                                            style={{ pointerEvents: selectedSlot === null ? 'none' : 'auto', opacity: selectedSlot === null ? 0.5 : 1 }}
                                        >
                                            {t(cmd.label)}
                                        </CommandItem>
                                    ))}
                                </CategoryBlock>
                            ))}
                        </CommandCategories>
                    </CommandList>
                </ContentArea>
            </div>
        </ModuleContainer>
    );
};

export default RadioModule;
